import React, { useEffect, useState, useMemo } from "react";
import DataGrid, {
  Column,
  FilterBuilder,
  FilterBuilderPopup,
  CustomOperation,
  HeaderFilter
} from 'devextreme-react/data-grid';
import { setInOperatorTextBoxValue } from 'components/explore/views/filterEditorHelpers';
import { connect } from "react-redux";
import CustomStore from 'devextreme/data/custom_store';
import {
  setExploreFilter
} from 'store/actions';
import { sendClientLog } from "services/logService";
import { postWithToken } from 'api/network';
import {
  customOperations,
  filterBuilderFields,
  isDateOrDateTime
} from 'components/explore/views/filterEditorHelpers';
import { dataGridExtension } from 'components/devExtreme';
import isPlainObject from 'lodash/isPlainObject';
import { parseISO, format } from 'date-fns';

const defaultRule = ['propertyType', 'anyof', ['SFD', 'SFA']];

function PropertyDataDetail({
  object,
  filter,
  setExploreFilter,
  filterBuilderOpen,
  setFilterBuilderOpen
}) {
  const [dataSource, setDataSource] = useState(null);

  useEffect(() => {
    createDataSource();
  }, []);

  const formatFilterValueDates = (value) => {
    if (!value || !Array.isArray(value)) return value;
    const updateArray = arr => {
      return arr.map(el => {
        if (el instanceof Date) {
          return format(el, "yyyy-MM-dd'T'HH:mm:ss.SSS");
        } else if (Array.isArray(el)) {
          return updateArray(el);
        }
        return el;
      })
    }
    return updateArray(value);
  }

  const onFilterGridOptionChanged = option => {
    if (option.name === 'filterValue') {
      const newFilterValue = option.component.option('filterValue');
      const newFilterExpression = option.component.getCombinedFilter(true);
      if (newFilterExpression && newFilterExpression !== "") {
        setExploreFilter(object.id, { filterValue: formatFilterValueDates(newFilterValue), filterExpression: newFilterExpression });
        sendClientLog('Info', `Explore Views - User applied data filter on filter builder for Property Data object id ${object.id}, layer id ${object.layerId}`);
      } else {
        setExploreFilter(object.id, { filterValue: null, filterExpression: null });
        sendClientLog('Info', `Explore Views - User applied null data filter on filter builder for Property Data object id ${object.id}, layer id ${object.layerId}`);
      }
    }
  }

  function getFilterBuilderCustomOperations() { 
    return customOperations().map(operation => { 
      return <CustomOperation 
        key={operation.name} 
        name={operation.name} 
        caption={operation.caption} 
        dataTypes={operation.dataTypes} 
        hasValue={operation.hasValue} 
        editorRender={operation.editorRender} 
        calculateFilterExpression={operation.calculateFilterExpression} 
        icon={null} 
      /> 
    }) 
  } 

  function createDataSource() {
    const url = '/api/explore/filterBuilderLookup';

    const store = new CustomStore({
      key: 'asgPropID',
      load: function (loadOptions) {
        if (!loadOptions.group) return new Promise((resolve) => {
          resolve({ data: [{asgPropID: 1}], totalCount: 1 });
        });

        if (isPlainObject(loadOptions.group)) {
          loadOptions.group = [loadOptions.group];
        }

        let params = {};

        dataGridExtension.customStoreRequestParams.forEach(i => {
          if (i in loadOptions && dataGridExtension.isNotEmpty(loadOptions[i])) {
            params[i] = loadOptions[i];
          }
        });

        return postWithToken(url, { loadOptions: params })
          .then(response => response.json())
          .then((data) => {
            return {
              data: data.data,
              totalCount: data.totalCount,
            };
          })
          .catch((error) => {
            // appNotify(`Failed to fetch Import Opportunity History: ${error.message}`, 'error');
            return { data: [], totalCount: 0 };
          })
      }
    });
    setDataSource(store);
  }

  const columns = useMemo(() => {
    const columnList = filterBuilderFields.map(({
      dataField,
      dataType,
      format
    }) => {
      return (
        <Column
          key={dataField}
          dataField={dataField}
          dataType={dataType}
          format={format}
          visible={false}
        >
          <HeaderFilter
            groupInterval={null}
            search={{
              enabled: dataType !== 'boolean',
              mode: dataType === 'number' ? 'startswith' : 'contains'
            }}
          />
        </Column>
      );
    });
    return columnList;
  }, []);

  const parseFilterBuilderValueDates = useMemo(() => {
    if (filter?.filterValue == null) return null;
    const updateArray = arr => {
      return arr.map(el => {
        if (typeof el === 'string' && isDateOrDateTime(el)) {
          return parseISO(el);
        } else if (Array.isArray(el)) {
          return updateArray(el);
        }
        return el;
      })
    }
    return updateArray(filter.filterValue);
  }, [filter.filterValue]);

  return (
    <DataGrid
      remoteOperations={true}
      dataSource={dataSource}
      height={0}
      width={0}
      showColumnHeaders={false}
      onOptionChanged={onFilterGridOptionChanged}
      loadPanel={{ enabled: false }}
      dateSerializationFormat="yyyy-MM-dd'T'HH:mm:ss.SSS"
    >
      <FilterBuilder
        fields={filterBuilderFields}
        value={parseFilterBuilderValueDates ?? defaultRule}
        maxGroupLevel={5}
        onEditorPreparing={setInOperatorTextBoxValue}
      >
        {getFilterBuilderCustomOperations()}
      </FilterBuilder>
      <FilterBuilderPopup visible={filterBuilderOpen} onHiding={() => setFilterBuilderOpen(false)}/>
      {columns}
    </DataGrid>
  );
}

function mapState(state, ownProps) {
  const { exploreData } = state;
  const propertyDataObject = exploreData.propertyDataObjects[ownProps.object.id];
  return {
    filter: propertyDataObject.filter
  };
}

const mapDispatch = {
  setExploreFilter
};

export default connect(mapState, mapDispatch)(PropertyDataDetail);